import React, { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  CardHeader,
  Heading,
  Link,
  Text,
  TextButton,
  Tooltip,
} from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import useChrome from '@chrome/hooks/useChrome';
import { ChromeKeys } from '@chrome/enums';
import RegistrationTitle from '@features/account-registration/components/RegistrationTitle';
import RegistrationUserDetails from '@features/account-registration/components/RegistrationUserDetails/RegistrationUserDetails';
import {
  RegistrationCardContent,
  RegistrationCustomDivider,
  RegistrationFormCard,
} from '@features/account-registration/style';
import {
  RegistrationPageFooterWrapper,
  RegistrationPageFooterActions,
  RegistrationPageFooterText,
} from '@features/account-registration/components/RegistrationFooter/style';
import {
  CAPTCHA_MODAL,
  CHALLENGE_IFRAME,
  REGISTRATION_FORM_FOOTER_CANCEL_BUTTON_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_MESSAGE_ADS_AGREEMENT_LINK_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_MESSAGE_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_SUBMIT_BUTTON_TRANSLATION_KEY,
  REGISTRATION_FORM_FOOTER_TOOLTIP_MESSAGE_TRANSLATION_KEY,
  TERMS_PAGE_URL,
} from '@features/account-registration/components/RegistrationFooter/constants';
import { DynamicText } from 'src/shared/components/DynamicText';
import Spinner, { SpinnerSize } from '@components/Spinner';
import InputController from '@components/form/InputController';
import * as formConstants from '@components/form/constants';
import {
  useCreateManagerAccountsMutation,
  useGetEnumerationsQuery,
  useGetManagerAccountRegistrationInfoQuery,
} from '@services/account/account';
import { useGetCaptchaQuery } from '@services/captcha';
import { logEvent } from '@utils/takt';
import {
  getErrorHeader,
  getErrorMessage,
} from '@features/account-registration/errors';
import {
  CREATE_ACCOUNT_FAILURE,
  REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
} from '@features/account-registration/constants';
import { ManagerAccountsPayload } from '@services/account/types';
import { CaptchaResponse } from '@models/captcha';
import CaptchaModal from '@components/modals/CaptchaModal';
import {
  ENTITY_NAME_MAX_LENGTH,
  NO_BLANK_SPACE_REGEX,
} from 'src/shared/constants';
import {
  ACCOUNT_DISPLAY_NAME_FIELD_EDIT_LABEL,
  ACCOUNT_NAME_FIELD_TOOLTIP_TRANSLATION_KEY,
  ACCOUNT_NAME_FIELD_ID,
  ACCOUNT_NAME_FIELD_CONTROLLER_NAME,
} from '@features/account-settings/constants';
import {
  ManagerRegistrationPageWrapper,
  ManagerRegistrationPageInnerWrapper,
} from '../managerAccountRegistration/style';
import { managerAccountLinkingUrl } from 'src/shared/routes';
import BusinessDetailsSection from './components/BusinessDetailsSection';
import {
  MANAGER_ACCOUNT_CREATION_PAGE_ID,
  MANAGER_ACCOUNT_CREATION_TITLE_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_DETAILS_HEADING_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_CREATED_NO_NAVIGATION_TRANSLATION_KEY,
  MANAGER_ACCOUNT_CREATION_SPINNER_TRANSLATION_KEY,
  INVALID_INPUT_ERROR_CODE,
  INVALID_INPUT_FIELD_LOCATION_TO_CODE,
  MANAGER_ACCOUNT_CREATION_FAILED_FALLBACK_CODE,
  MANAGER_ACCOUNT_ERROR_ALERT_MAPPING,
  MANAGER_ACCOUNT_REFUSED_FALLBACK_CODE,
} from './constants';

const hasAlertCopy = (code: string): boolean =>
  Object.prototype.hasOwnProperty.call(
    MANAGER_ACCOUNT_ERROR_ALERT_MAPPING,
    code,
  );

interface ErrorAlert {
  header: ReactNode;
  message: ReactNode;
}

interface ManagerAccountCreationFormValues {
  displayName: string;
  business: {
    businessName: string;
    phone: string;
    website: string;
    countryCode: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
  };
  industry: string;
  timeZone: string;
}

/**
 * Step 1 of the ARMS-owned manager account creation flow: collects a display
 * name, business details, timezone and industry, then creates the manager
 * account through GRS (single-item batch). On success it hands off to step
 * 2, the AUMS-hosted account-linking page.
 */
const ManagerAccountCreationPage: FC = () => {
  const { intl } = useIntl();
  const [createManagerAccounts] = useCreateManagerAccountsMutation();
  const {
    data: enumerations,
    isFetching: isFetchingEnumerations,
    isError: isEnumerationsError,
    refetch: refetchEnumerations,
  } = useGetEnumerationsQuery({});
  // The welcome header is informational: on a failed load the page renders
  // without it rather than blocking the form, which does not depend on it
  const { data: registrationInfo } =
    useGetManagerAccountRegistrationInfoQuery();
  // Same anti-automation gate as the sponsored ads registration page. The
  // config is read on its own so a failed load degrades to a plain submit
  // (as that page does when captchaEnabled is false) instead of blocking
  // the form; the service still decides whether to enforce.
  const { data: captcha } = useGetCaptchaQuery();
  // Form values validated and waiting on the challenge; set while the
  // captcha modal is open, cleared once the challenge callback submits them
  const [pendingSubmission, setPendingSubmission] =
    useState<ManagerAccountCreationFormValues | null>(null);
  const [error, setError] = useState<ErrorAlert | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // Set once the account was created but could not be navigated to: a
  // resubmit would create a duplicate, so submit is locked for good
  const [isCreatedWithoutNavigation, setIsCreatedWithoutNavigation] =
    useState(false);
  // Synchronous double-submit guard: mutation isLoading updates a render too
  // late to stop a rapid second submit from creating a duplicate account.
  const isSubmittingRef = useRef(false);

  useChrome(ChromeKeys.ManagerAccountCreation);

  const methods = useForm<ManagerAccountCreationFormValues>({
    mode: 'onBlur',
    defaultValues: {
      displayName: '',
      business: {
        businessName: '',
        phone: '',
        website: '',
        countryCode: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
      },
      industry: '',
      timeZone: '',
    },
    // The state field remounts between a dropdown and a text input as the
    // country changes; without this the unmounted variant's registration
    // and any error it held would survive and block submit invisibly
    shouldUnregister: true,
  });

  // Every required field, including the business details, gates submit, so
  // the Create button mirrors the form's own validity the way the sponsored
  // ads footer does: react-hook-form resolves the required rules (including
  // the state dropdown's country-dependent one) with mode onBlur.
  const { isValid } = methods.formState;
  const canSubmit = isValid && !isCreating && !isCreatedWithoutNavigation;

  // Mirrors the sponsored ads registration page: the service's code is
  // logged as the failure reason, and picks the alert copy when the page
  // explains it; otherwise the fallback's copy is shown. An INVALID_INPUT
  // that names the refused address field gets that field's copy instead
  const showError = useCallback(
    (
      serviceCode: string | undefined,
      fallbackCode: string,
      fieldLocation?: string,
    ) => {
      logEvent(
        `${MANAGER_ACCOUNT_CREATION_PAGE_ID}-${CREATE_ACCOUNT_FAILURE}`,
        { reason: serviceCode ?? fallbackCode, fieldLocation },
      );
      const fieldCode =
        serviceCode === INVALID_INPUT_ERROR_CODE && fieldLocation
          ? INVALID_INPUT_FIELD_LOCATION_TO_CODE[fieldLocation]
          : undefined;
      const resolvedCode = fieldCode ?? serviceCode;
      const copyCode =
        resolvedCode && hasAlertCopy(resolvedCode)
          ? resolvedCode
          : fallbackCode;
      setError({
        header: getErrorHeader(
          intl,
          copyCode,
          MANAGER_ACCOUNT_ERROR_ALERT_MAPPING,
        ),
        // dspEntityId is only meaningful on the sponsored ads page
        message: getErrorMessage(
          intl,
          copyCode,
          undefined,
          MANAGER_ACCOUNT_ERROR_ALERT_MAPPING,
        ),
      });
    },
    [intl],
  );

  const createAccount = useCallback(
    async (
      formData: ManagerAccountCreationFormValues,
      captchaResponse?: CaptchaResponse,
    ) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      setIsCreating(true);
      setError(null);
      try {
        const payload: ManagerAccountsPayload = {
          displayName: formData.displayName,
          business: {
            businessName: formData.business.businessName,
            phone: formData.business.phone,
            website: formData.business.website,
            countryCode: formData.business.countryCode,
            addressLine1: formData.business.addressLine1,
            ...(formData.business.addressLine2 && {
              addressLine2: formData.business.addressLine2,
            }),
            city: formData.business.city,
            ...(formData.business.state && {
              state: formData.business.state,
            }),
            zipCode: formData.business.zipCode,
          },
          industry: formData.industry,
          timeZone: formData.timeZone,
        };
        const result = await createManagerAccounts({
          data: payload,
          ...(captchaResponse && { captchaResponse }),
        }).unwrap();
        const successItem = result.success?.[0];
        const linkingUrl = managerAccountLinkingUrl(
          successItem?.managerAccount?.managerAccountId,
          window.location.search,
        );
        if (linkingUrl) {
          // Step 2 (account linking) is hosted by AUMS, outside this SPA's
          // router, so this is a full page navigation. The submit guard and
          // Loading state are deliberately held until the page unloads;
          // releasing them here would reopen the double-submit window during
          // the navigation gap.
          window.location.assign(linkingUrl);
          return;
        }
        if (successItem) {
          // The account WAS created but carried no id to navigate with, so a
          // retry would create a duplicate: say so, and lock submit for good
          // (the lock feeds canSubmit, which drives the Create button; the
          // ref guard stays set as a second line of defence)
          setIsCreatedWithoutNavigation(true);
          setIsCreating(false);
          setError({
            header: intl.getMessage(
              REGISTRATION_GENERAL_ERROR_BANNER_TITLE_TRANSLATION_KEY,
            ),
            message: (
              <Text>
                {intl.getMessage(
                  MANAGER_ACCOUNT_CREATION_CREATED_NO_NAVIGATION_TRANSLATION_KEY,
                )}
              </Text>
            ),
          });
          return;
        }
        // No success item: GRS refused the request as a per-item error
        // under HTTP 201. The item's code names what to change when it is
        // one the page explains; otherwise the input still needs to change
        // before a retry can work
        const refusal = result.error?.[0]?.errors?.[0];
        showError(
          refusal?.code,
          MANAGER_ACCOUNT_REFUSED_FALLBACK_CODE,
          refusal?.fieldLocation,
        );
      } catch (error) {
        console.error('Manager account creation failed', error);
        // A request-level 400 from the BFF carries a top-level errorCode
        // (the manager-account limit, or INVALID_INPUT when GRS rejected
        // the request outright); anything else is a transport or service
        // failure worth retrying as is
        const errorCode = (error as { data?: { errorCode?: string } })?.data
          ?.errorCode;
        showError(errorCode, MANAGER_ACCOUNT_CREATION_FAILED_FALLBACK_CODE);
      }
      // Transport/HTTP failure or GRS refusal only: the success and
      // created-but-no-id paths never reach here
      setIsCreating(false);
      isSubmittingRef.current = false;
    },
    [createManagerAccounts, intl, showError],
  );

  // The form is already valid at this point; it is parked until the challenge
  // is solved. No submit lock is taken while the modal is open: the challenge
  // owns the interaction until it is solved, so there is nothing to guard.
  const onSubmit = useCallback(
    (formData: ManagerAccountCreationFormValues) => {
      if (captcha?.captchaEnabled) {
        setPendingSubmission(formData);
        return;
      }
      return createAccount(formData);
    },
    [captcha?.captchaEnabled, createAccount],
  );

  const onCaptchaSolved = useCallback(
    (captchaResponse: CaptchaResponse) => {
      if (!captchaResponse || !pendingSubmission) {
        return;
      }
      const formData = pendingSubmission;
      setPendingSubmission(null);
      return createAccount(formData, captchaResponse);
    },
    [createAccount, pendingSubmission],
  );

  // Same label as the sponsored ads registration footer, so both pages read
  // "Accept and continue"
  const submitLabel = intl.getMessage(
    REGISTRATION_FORM_FOOTER_SUBMIT_BUTTON_TRANSLATION_KEY,
  );

  return (
    <ManagerRegistrationPageWrapper
      id={MANAGER_ACCOUNT_CREATION_PAGE_ID}
      data-testid={MANAGER_ACCOUNT_CREATION_PAGE_ID}
    >
      <ManagerRegistrationPageInnerWrapper>
        {isCreating && (
          <Spinner
            isFullScreen={true}
            text={intl.getMessage(
              MANAGER_ACCOUNT_CREATION_SPINNER_TRANSLATION_KEY,
            )}
            size={SpinnerSize.large}
            withBackground={true}
          />
        )}
        {error && (
          <Alert
            variant="error"
            header={error.header}
            withCloseButton
            onClose={() => setError(null)}
          >
            {error.message}
          </Alert>
        )}
        <CaptchaModal
          id={`${MANAGER_ACCOUNT_CREATION_PAGE_ID}-${CAPTCHA_MODAL}`}
          isOpen={pendingSubmission !== null}
          captchaOptions={{
            ...captcha,
            'data-event': new CustomEvent('customEvent'),
            'data-iframe-id': `${MANAGER_ACCOUNT_CREATION_PAGE_ID}-${CHALLENGE_IFRAME}`,
            'data-modal': false,
            'data-callback': onCaptchaSolved,
          }}
        />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <RegistrationTitle
              title={intl.getMessage(
                MANAGER_ACCOUNT_CREATION_TITLE_TRANSLATION_KEY,
              )}
            />
            {registrationInfo && (
              <RegistrationFormCard padding="medium">
                <RegistrationUserDetails
                  userName={registrationInfo.userName}
                  emailAddress={registrationInfo.emailAddress}
                  switchAccountUrl={registrationInfo.switchAccountUrl}
                />
              </RegistrationFormCard>
            )}
            <RegistrationFormCard>
              <CardHeader padding="medium">
                <Heading renderAs="h2" size="xlarge">
                  {intl.getMessage(
                    MANAGER_ACCOUNT_CREATION_DETAILS_HEADING_TRANSLATION_KEY,
                  )}
                </Heading>
              </CardHeader>
              <RegistrationCardContent>
                <BusinessDetailsSection
                  enumerations={enumerations}
                  isFetchingEnumerations={isFetchingEnumerations}
                  isEnumerationsError={isEnumerationsError}
                  refetchEnumerations={refetchEnumerations}
                />
              </RegistrationCardContent>
              <RegistrationCustomDivider />
              <RegistrationCardContent>
                <InputController
                  label={intl.getMessage(ACCOUNT_DISPLAY_NAME_FIELD_EDIT_LABEL)}
                  id={ACCOUNT_NAME_FIELD_ID}
                  controllerName={ACCOUNT_NAME_FIELD_CONTROLLER_NAME}
                  registerOptions={{
                    required: intl.getMessage(
                      formConstants.REQUIRED_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
                    ),
                    maxLength: {
                      value: ENTITY_NAME_MAX_LENGTH,
                      message: intl.formatMessage(
                        formConstants.MAX_LENGTH_BUSINESS_NAME_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
                        { maxLength: ENTITY_NAME_MAX_LENGTH.toString() },
                      ),
                    },
                    pattern: {
                      value: NO_BLANK_SPACE_REGEX,
                      message: intl.getMessage(
                        formConstants.REQUIRED_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
                      ),
                    },
                    validate: (value: string) => {
                      // Mirrors GRS's phone-number blocker so the refusal is
                      // explained here instead of a generic per-item error
                      if (
                        formConstants.PHONE_NUMBER_IN_NAME_VALIDATION.test(
                          value,
                        )
                      ) {
                        return intl.getMessage(
                          formConstants.PHONE_NUMBER_IN_NAME_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
                        );
                      }
                      return true;
                    },
                  }}
                  helpTipText={intl.getMessage(
                    ACCOUNT_NAME_FIELD_TOOLTIP_TRANSLATION_KEY,
                  )}
                />
              </RegistrationCardContent>
            </RegistrationFormCard>
            <RegistrationPageFooterWrapper>
              <RegistrationPageFooterText>
                <DynamicText
                  message={intl.getMessage(
                    REGISTRATION_FORM_FOOTER_MESSAGE_TRANSLATION_KEY,
                  )}
                  resolveComponent={() => (
                    <Link href={TERMS_PAGE_URL}>
                      {intl.getMessage(
                        REGISTRATION_FORM_FOOTER_MESSAGE_ADS_AGREEMENT_LINK_TRANSLATION_KEY,
                      )}
                    </Link>
                  )}
                />
              </RegistrationPageFooterText>
              <RegistrationPageFooterActions>
                <TextButton
                  onClick={() => {
                    window.location.href = '/';
                  }}
                >
                  {intl.getMessage(
                    REGISTRATION_FORM_FOOTER_CANCEL_BUTTON_TRANSLATION_KEY,
                  )}
                </TextButton>
                {canSubmit ? (
                  <Button variant="primary" type="submit">
                    {submitLabel}
                  </Button>
                ) : (
                  <Tooltip
                    trigger={
                      <div>
                        <Button variant="primary" disabled>
                          {submitLabel}
                        </Button>
                      </div>
                    }
                    message={intl.getMessage(
                      REGISTRATION_FORM_FOOTER_TOOLTIP_MESSAGE_TRANSLATION_KEY,
                    )}
                    variant="inverted"
                  />
                )}
              </RegistrationPageFooterActions>
            </RegistrationPageFooterWrapper>
          </form>
        </FormProvider>
      </ManagerRegistrationPageInnerWrapper>
    </ManagerRegistrationPageWrapper>
  );
};

export default ManagerAccountCreationPage;
